import { Box } from '@razorpay/blade/components';
import api from 'api';
import {
  WizardError,
  WizardHeader,
  WizardLoader,
  WizardProgressView,
  WizardWrapper,
} from 'components/WizardViews/components';
import { WizardInfoProvider } from 'components/WizardViews/providers';
import { startOfMonth } from 'date-fns';
import { useFormWithProvider } from 'forms';
import { Wizard, WizardStep } from 'forms/Wizard';
import { useSearchParams } from 'hooks/useSearchParams';
import React from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import WizardPathToStepController from '../components/WizardPathToStepController';
import ReviewStep from './ReviewStep';
import SalaryDetailsStep from './SalaryDetailsStep';
import { revisePayRouteToStepMap, revisePaySteps, ReviseSalaryFormData } from './constants';

const RevisePayWizard = () => {
  const history = useHistory();

  const { setFormData } = useFormWithProvider<ReviseSalaryFormData>();

  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId) as number;

  const userId = useSearchParams().urlParams.get('userId') ?? loggedInUserId.toString();

  const payrollSetupQuery = useQuery({
    queryFn: () => api.userQueries.payrollGetSetup(),
    onSuccess: () => ctcQuery.refetch(),
  });

  const ctcQuery = useQuery({
    queryKey: ['getSalaryDetails', userId],
    queryFn: () =>
      api.userQueries.getSalaryDetails({
        userId,
      }),
    enabled: false,
    onSuccess: (data) => {
      if (!data.payrollGetSalaryDetails) return;
      const payrollDetails = data.payrollGetSalaryDetails.data;
      const payrollSetupInfo = payrollSetupQuery.data!.payrollGetSetup.data;

      setFormData({
        currentAnnualCtc: payrollDetails.annualSalary,
        currentCtcEffectiveFrom: payrollDetails.effectiveFromDate,
        payrollSetupInfo,
        effectiveFrom: startOfMonth(new Date()),
      });
    },
  });

  const handleCloseClick = () => {
    history.goBack();
  };

  if (ctcQuery.isFetching || payrollSetupQuery.isFetching || !ctcQuery.data) {
    return <WizardLoader />;
  }

  const error = ctcQuery.error || payrollSetupQuery.error;

  const payrollDetails = ctcQuery.data.payrollGetSalaryDetails.data;
  const name = payrollDetails.name;
  const firstName = name?.split(' ')[0];

  return (
    <WizardInfoProvider initialStep={0} totalSteps={2}>
      <WizardPathToStepController routeStepMap={revisePayRouteToStepMap} />
      <WizardWrapper>
        <WizardHeader
          heading="Revise compensation for"
          subHeading={firstName ?? 'Employee'}
          onCloseClick={handleCloseClick}>
          <WizardProgressView
            steps={[
              { label: 'Salary Details', position: revisePaySteps.step1 },
              { label: 'Review', position: revisePaySteps.step2 },
            ]}
          />
        </WizardHeader>
        <Box flex={1} overflowY="auto" height="100%">
          <WizardError onRetry={payrollSetupQuery.refetch} error={error}>
            <Wizard>
              <WizardStep step={revisePaySteps.step1} component={SalaryDetailsStep} />
              <WizardStep step={revisePaySteps.step2} component={ReviewStep} />
            </Wizard>
          </WizardError>
        </Box>
      </WizardWrapper>
    </WizardInfoProvider>
  );
};

export default RevisePayWizard;
